/**
 * Documents tab state and actions for the school management screen (ADM-8):
 * list, upload (with the framework-link sheet, ADM-9) and delete.
 */
import React, { useState } from 'react';

import { confirmDialog, showDialog } from '@/utils/dialogs';
import type { ToastType } from '@/components/ui/Toast';
import { apiRequest } from '../../services/apiClient';
import { documentUploadFields } from '../../components/school/documentFrameworkLinking';
import type { SchoolFramework } from '../../types/schoolFramework';
import { getSchoolApiBase } from './schoolApiBase';
import type { School, SchoolDocument } from './schoolManagementTypes';

type TFn = (key: string, defaultOrVars?: string | Record<string, any>, vars?: Record<string, any>) => string;

export function useSchoolDocuments({
  school,
  canAccessPrivateSchoolKnowledge,
  loadLinkableFrameworks,
  t,
  showToast,
}: {
  school: School | null;
  canAccessPrivateSchoolKnowledge: boolean;
  /** Frameworks the upload sheet offers for linking. */
  loadLinkableFrameworks: () => Promise<SchoolFramework[]>;
  t: TFn;
  showToast: (message: string, type?: ToastType) => void;
}) {
  const [documents, setDocuments] = useState<SchoolDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [pendingDocumentUpload, setPendingDocumentUpload] = useState<{
    file: File;
    frameworks: SchoolFramework[];
  } | null>(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);

  // Document Management Functions
  const loadDocuments = React.useCallback(async () => {
    if (!school || !canAccessPrivateSchoolKnowledge) {
      setDocuments([]);
      return;
    }

    try {
      setLoadingDocuments(true);
      const data = await apiRequest({
        endpoint: `/school-documents/${school.id}/documents`,
        method: 'GET'
      });
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      showDialog(t('common.error'), t('schoolManagement.documentsSection.loadError'));
    } finally {
      setLoadingDocuments(false);
    }
  }, [canAccessPrivateSchoolKnowledge, school, t]);

  const uploadDocumentFile = async (file: File, linkedFrameworkIds: number[], available: SchoolFramework[]) => {
    if (!school) return;
    try {
      setUploadingDocument(true);
      const API = getSchoolApiBase();
      const { getAccessToken, USE_COOKIES } = await import('../../services/api');
      const token = USE_COOKIES ? null : await getAccessToken();

      const formData = new FormData();
      formData.append('file', file);
      const fields = documentUploadFields(linkedFrameworkIds, available);
      formData.append('coaching_method', fields.coaching_method);
      if (fields.linked_framework_ids) {
        formData.append('linked_framework_ids', fields.linked_framework_ids);
      }

      const response = await fetch(
        `${API}/api/school-documents/${school.id}/documents/upload`,
        {
          method: 'POST',
          credentials: USE_COOKIES ? 'include' : 'same-origin',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      showToast(t('schoolManagement.documentsSection.uploadSuccess'), 'success');
      await loadDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      showDialog(t('common.error'), t('schoolManagement.documentsSection.uploadError'));
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!school) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.txt,.docx';
    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (!file) return;

      const available = await loadLinkableFrameworks();
      if (!available.length) {
        await uploadDocumentFile(file, [], available);
        return;
      }
      // Ask which frameworks to link in a sheet (ADM-9); the sheet's
      // callbacks run the upload or drop the file.
      setPendingDocumentUpload({ file, frameworks: available });
    };
    input.click();
  };

  const cancelPendingDocumentUpload = () => setPendingDocumentUpload(null);

  const confirmPendingDocumentUpload = (frameworkIds: number[]) => {
    const pending = pendingDocumentUpload;
    setPendingDocumentUpload(null);
    if (pending) void uploadDocumentFile(pending.file, frameworkIds, pending.frameworks);
  };

  const handleDeleteDocument = async (documentId: number, filename: string) => {
    if (!school) return;

    const confirmationMessage = t('schoolManagement.documentsSection.deleteConfirm', {
      filename,
    });
    const confirmed = await confirmDialog(
      t('schoolManagement.documentsSection.deleteTitle'),
      confirmationMessage,
      t('common.delete'),
      t('common.cancel'),
    );

    if (!confirmed) return;

    try {
      setDeletingDocumentId(documentId);
      await apiRequest({
        endpoint: `/school-documents/${school.id}/documents/${documentId}`,
        method: 'DELETE'
      });

      showToast(t('schoolManagement.documentsSection.deleteSuccess'), 'success');
      await loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      showDialog(t('common.error'), t('schoolManagement.documentsSection.deleteError'));
    } finally {
      setDeletingDocumentId(null);
    }
  };

  return {
    documents,
    loadingDocuments,
    uploadingDocument,
    pendingDocumentUpload,
    deletingDocumentId,
    loadDocuments,
    handleUploadDocument,
    cancelPendingDocumentUpload,
    confirmPendingDocumentUpload,
    handleDeleteDocument,
  };
}

export type SchoolDocumentsState = ReturnType<typeof useSchoolDocuments>;
